import {
  EntityUpdateDialog,
  PropertyType,
} from '@internal/plugin-catalog-page-addons-react';
import ApiIcon from '@mui/icons-material/Api';
import GroupIcon from '@mui/icons-material/Group';
import React, { useCallback, useState } from 'react';
import { useAsync } from 'react-use';

import { Entity } from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';

import { Button } from '@material-ui/core';
import GitHubIcon from '@material-ui/icons/GitHub';

const isService = (entity: Entity) =>
  entity.kind.toLowerCase() === 'component' &&
  `${entity?.spec?.type}`?.toLowerCase() === 'service';

const toOptionArray = (entities: Entity[]) => {
  return (
    entities.map(entity => ({
      id: entity.metadata.name,
      title: entity.metadata.title || entity.metadata.name,
    })) ?? []
  );
};

export const ComponentDetailsContent = () => {
  const { entity } = useEntity();
  const [annotationsDialogOpen, setAnnotationsDialogOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<PropertyType>('OWNER');
  const [dialogTitle, setDialogTitle] = useState('Update Owner');
  const catalogApi = useApi(catalogApiRef);
  const { value: groups } = useAsync(async () => {
    const result = await catalogApi.getEntities({ filter: { kind: 'Group' } });
    return result.items;
  }, [catalogApi]);

  const { value: repositories } = useAsync(async () => {
    const result = await catalogApi.getEntities({
      filter: { kind: 'Repository' },
    });
    return result.items;
  }, [catalogApi]);

  const { value: apis } = useAsync(async () => {
    const result = await catalogApi.getEntities({ filter: { kind: 'API' } });
    return result.items;
  }, [catalogApi]);

  // Build owners list
  const ownerOptions = toOptionArray(groups ?? []);
  const repositoryOptions = toOptionArray(repositories ?? []);
  const apiOptions = toOptionArray(apis ?? []);

  const getOptionsForType = useCallback(
    (property: PropertyType) => {
      if (property === 'OWNER') {
        return ownerOptions;
      }
      if (property === 'REPOSITORIES') {
        return repositoryOptions;
      }
      if (property === 'APIS') {
        return apiOptions;
      }
      return [];
    },
    [ownerOptions, apiOptions, repositoryOptions],
  );

  const handleCloseAnnotationsDialog = () => {
    setAnnotationsDialogOpen(false);
  };

  const onClick = useCallback((property: PropertyType) => {
    setSelectedType(property);
    setDialogTitle(_ => {
      if (property === 'OWNER') {
        return 'Update Owner';
      }
      if (property === 'REPOSITORIES') {
        return 'Update Service Repositories';
      }
      if (property === 'APIS') {
        return 'Update Service APIs';
      }
      return '';
    });
    setAnnotationsDialogOpen(true);
  }, []);

  return (
    isService(entity) && (
      <>
        <Button startIcon={<GroupIcon />} onClick={() => onClick('OWNER')}>
          Update Owner
        </Button>
        <Button
          startIcon={<GitHubIcon />}
          onClick={() => onClick('REPOSITORIES')}
        >
          Update Service Repositories
        </Button>
        <Button startIcon={<ApiIcon />} onClick={() => onClick('APIS')}>
          Update Service APIs
        </Button>
        <EntityUpdateDialog
          dialogTitle={dialogTitle}
          open={annotationsDialogOpen}
          onClose={handleCloseAnnotationsDialog}
          property={
            {
              propertyType: selectedType,
            } as any
          }
          propertyOptions={getOptionsForType(selectedType)}
        />
      </>
    )
  );
};
